import { Icon, Tooltip } from "@components/library";
import { TOOLTIP_CONTENT } from "@components/RequestPageComponents/tooltipContent";
import toast from "@components/Toast";
import { COLORS, STYLES } from "@constants";
import useLocalStorage from "@hooks/useLocalStorage";
import { RootState } from "@redux/store";
import {
  deleteUserRequestPreference,
  getUserRequestPreference,
  updateUserRequestPreference,
} from "@requests/user_request_preferences";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SegmentEventName } from "@tsTypes/__generated__/enums";
import { UserRequestPreferenceSource } from "@tsTypes/__generated__/enums/user_request_preference_source";
import { RequestForCard } from "@tsTypes/index";
import { MarketplaceRequest } from "@tsTypes/requests/marketplace";
import appsignal from "@utils/appsignal";
import { track } from "@utils/appUtils";
import { t } from "@utils/i18n";
import React, { Dispatch, SetStateAction, useEffect } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";

interface RefineRecommendationsProps {
  request: RequestForCard | MarketplaceRequest;
  trackingProps?: Record<string, any>;
  setShouldFade: Dispatch<SetStateAction<boolean>>;
}

function useRequestPreferences(request: RequestForCard | MarketplaceRequest, isLoggedIn: boolean) {
  const [localPreference, setLocalPreference] = useLocalStorage<boolean | null>(
    `request_preference_${request.id}`,
    null
  );

  const queryClient = useQueryClient();
  const queryKey = ["userRequestPreference", request.id, isLoggedIn, localPreference];

  const { data: isInterestedQuery } = useQuery({
    queryKey,
    queryFn: async () => {
      if (isLoggedIn) {
        const preference = await getUserRequestPreference(request.id);
        return preference?.is_interested ?? null;
      }
      return localPreference;
    },
    initialData: request.user_request_preference?.is_interested,
    // Prevent automatic refetching
    // This allows us to use the initial data from request.user_request_preference
    // and only update the data when mutations explicitly invalidate the query
    staleTime: Infinity,
  });

  const { mutate: updateInterest } = useMutation({
    mutationFn: async (isInterested: boolean) => {
      if (isLoggedIn)
        return updateUserRequestPreference({
          requestId: request.id,
          isInterested,
          source: UserRequestPreferenceSource.MARKETPLACE_CARD,
        });
      setLocalPreference(isInterested);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(queryKey);
    },
    onError: (error: Error) => {
      appsignal.sendError(error, (span) => {
        span.setAction("useRequestPreferences#updateInterest");
        span.setTags({
          requestId: String(request.id),
        });
      });
    },
  });

  const { mutate: deleteInterest } = useMutation({
    mutationFn: async () => {
      if (isLoggedIn) return deleteUserRequestPreference(request.id);
      setLocalPreference(null);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(queryKey);
    },
    onError: (error: Error) => {
      appsignal.sendError(error, (span) => {
        span.setAction("useRequestPreferences#deleteInterest");
        span.setTags({
          requestId: String(request.id),
        });
      });
    },
  });

  return {
    isInterested: isInterestedQuery,
    updateInterest,
    deleteInterest,
  };
}

const i18nPrefix = "components.refine_recommendations";

const RefineRecommendations: React.FC<RefineRecommendationsProps> = ({
  request,
  trackingProps = {},
  setShouldFade,
}) => {
  const currentUser = useSelector((state: RootState) => state.profiles.currentUser);
  const isLoggedIn = currentUser.isLoggedIn;

  const { isInterested, updateInterest, deleteInterest } = useRequestPreferences(
    request,
    isLoggedIn
  );

  const handleRelevantClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    track(SegmentEventName.CLICK, {
      react_component: "RefineRecommendations",
      ui_component: "Relevant button",
      request_slug: request.slug,
      is_logged_in: isLoggedIn,
      ...trackingProps,
    });

    updateInterest(true);

    toast.success(
      <div>
        {t([i18nPrefix, "toasts.relevant"])}{" "}
        <UndoButton onClick={deleteInterest}>{t([i18nPrefix, "buttons.undo"])}</UndoButton>
      </div>
    );
  };

  const handleNotRelevantClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    track(SegmentEventName.CLICK, {
      react_component: "RefineRecommendations",
      ui_component: "Not relevant button",
      request_slug: request.slug,
      is_logged_in: isLoggedIn,
      ...trackingProps,
    });

    updateInterest(false);

    toast.success(
      <div>
        {t([i18nPrefix, "toasts.not_relevant"])}{" "}
        <UndoButton onClick={deleteInterest}>{t([i18nPrefix, "buttons.undo"])}</UndoButton>
      </div>
    );
  };

  useEffect(() => {
    setShouldFade(isInterested === false);
  }, [isInterested]);

  return (
    <ButtonContainer>
      <Tooltip
        position="right"
        tooltipWidth="200px"
        arrowOffsetTop="36px"
        remainOnHover={false}
        asPortal
        content={TOOLTIP_CONTENT.RELEVANT.NOT_SIGNED_IN}
      >
        <RelevanceButton
          onClick={handleRelevantClick}
          $selected={isInterested}
          aria-label={t([i18nPrefix, "buttons.relevant.aria_label"])}
        >
          <Icon name="Checkmark" color={COLORS.BLUE_500} size="md" />
        </RelevanceButton>
      </Tooltip>
      <Tooltip
        position="right"
        tooltipWidth="275px"
        arrowOffsetTop="45px"
        remainOnHover={false}
        asPortal
        content={TOOLTIP_CONTENT.NOT_RELEVANT}
      >
        <RelevanceButton
          onClick={handleNotRelevantClick}
          $selected={isInterested === false}
          aria-label={t([i18nPrefix, "buttons.not_relevant.aria_label"])}
        >
          <Icon name="Block" color={COLORS.NEUTRAL_500} size="md" />
        </RelevanceButton>
      </Tooltip>
    </ButtonContainer>
  );
};

export default RefineRecommendations;

const RelevanceButton = styled.button`
  position: relative;

  display: flex;
  justify-content: center;
  align-items: center;

  width: 40px;
  height: 34px;

  box-shadow: ${STYLES.SHADOW_CARD};
  border: none;

  outline: 1px solid ${({ $selected }) => ($selected ? COLORS.HALO_BLUE : COLORS.NEUTRAL_250)};
  background: ${({ $selected }) => ($selected ? COLORS.BLUE_LIGHT_300 : COLORS.WHITE)};
  ${({ $selected }) => $selected && `z-index: 1;`}

  &:hover {
    outline-color: ${COLORS.BLUE_450};
    background-color: ${({ $selected }) => ($selected ? COLORS.BLUE_LIGHT_50 : COLORS.NEUTRAL_50)};
    z-index: 1;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;

  & > div:first-child ${RelevanceButton} {
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;
  }

  & > div:last-child ${RelevanceButton} {
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;
    color: ${COLORS.NEUTRAL_500};
  }
`;

const UndoButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-decoration: underline;
`;
